import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import {
    Box, Paper, Typography, Divider, Button, Grid, 
    IconButton, Stack, CircularProgress
} from '@mui/material';
import {
    LocalPrintshop as LocalPrintshopIcon,
    AirplayOutlined as AirplayOutlinedIcon,
    FileDownloadOutlined as FileDownloadOutlinedIcon,
    SyncAltOutlined as SwitchIcon,
    SettingsOutlined as SettingsIcon
} from '@mui/icons-material';
import { LABELGENAPI } from '../../../API'; // Adjust this path match to point to your axios instance
import { setQzStateInfo, setQzNotifications, setQzInProgress } from '../../../qzSlice'; // Adjust this path to your slice location

const qz = require("qz-tray");

// --- Enterprise Styled Components ---
const SettingsContainer = styled(Paper)(({ theme }) => ({
    borderRadius: '16px',
    border: '1px solid #E2E8F0',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
    padding: theme.spacing(4),
    backgroundColor: '#FFFFFF',
    position: 'relative',
    width: '100%',
    boxSizing: 'border-box'
}));

const StatusBlock = styled(Box)(({ theme, isActive }) => ({
    padding: theme.spacing(3),
    borderRadius: '12px',
    border: '1px solid',
    borderColor: isActive ? '#E2E8F0' : '#E2E8F0',
    backgroundColor: isActive ? 'rgba(46, 125, 50, 0.02)' : '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    transition: 'all 0.2s ease-in-out',
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
    }
}));

const SectionHeader = ({ icon, title, subtitle }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ 
            p: 1.5, 
            bgcolor: 'rgba(25, 118, 210, 0.06)', 
            borderRadius: '10px', 
            color: '#1976d2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            {icon}
        </Box>
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '16px', m: 0 }}>
                {title}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500, fontSize: '13px', mt: 0.5, display: 'block' }}>
                {subtitle}
            </Typography>
        </Box>
    </Box>
);

const QZTrayPage = () => {
    const dispatch = useDispatch();
    const qzState = useSelector(state => state.qz);
    const [localLoading, setLocalLoading] = useState(false);

    // --- Isolated Security Handshake Routine ---
    const verifyQzSecuritySignature = () => {
        qz.security.setCertificatePromise((resolve, reject) => {
            LABELGENAPI.get("/security/digital-certificate")
                .then(response => resolve(response.data))
                .catch(error => reject(error.response));
        });
        
        qz.security.setSignatureAlgorithm("SHA512");
        qz.security.setSignaturePromise((toSign) => {
            return (resolve, reject) => {
                LABELGENAPI.get("/security/private-key?data=" + toSign)
                    .then(response => resolve(response.data))
                    .catch(error => reject(error.response));
            };
        });
    };

    // --- Core Connection Engine ---
    const handleConnectQZ = () => {
        if (qz.websocket.isActive()) {
            return;
        }

        setLocalLoading(true);
        dispatch(setQzInProgress());
        verifyQzSecuritySignature();

        qz.websocket.connect()
            .then(() => {
                dispatch(setQzStateInfo({
                    "is_active": true,
                    "qz": qz,
                    "printer": ""
                }));

                qz.websocket.setClosedCallbacks(() => {
                    dispatch(setQzStateInfo({
                        "is_active": false,
                        "printer": "",
                        "qz": ""
                    }));
                });

                return qz.printers.getDefault();
            })
            .then((printer) => {
                if (printer && printer !== "undefined" && typeof printer === "string" && printer.length !== 0) {
                    dispatch(setQzStateInfo({
                        "is_active": true,
                        "printer": printer,
                        "qz": qz
                    }));
                }
            })
            .catch((e) => {
                dispatch(setQzStateInfo({
                    "is_active": false,
                    "printer": "",
                    "qz": ""
                }));
                
                dispatch(setQzNotifications({
                    "message": ["Unable to establish connection with local QZ Tray client. Please ensure the desktop app is open and running."],
                    "error": true
                }));
                console.warn("QZ Tray Client connection blocked safely:", e.message);
            })
            .finally(() => {
                setLocalLoading(false);
            });
    };

    // --- Core Disconnect Engine ---
    const handleDisconnectQZ = () => {
        if (qzState.queue_count > 0) {
            dispatch(setQzNotifications({
                "message": ["Please wait for pending queues to be printed"],
                "error": true
            }));
            return;
        }

        if (qz.websocket.isActive()) {
            setLocalLoading(true);
            qz.websocket.disconnect()
                .then(() => {
                    dispatch(setQzStateInfo({
                        "is_active": false,
                        "printer": "",
                        "qz": ""
                    }));
                })
                .catch((e) => {
                    dispatch(setQzNotifications({
                        "message": [e.message],
                        "error": true
                    }));
                })
                .finally(() => {
                    setLocalLoading(false);
                });
        } else {
            // Force reset state if local socket was closed un-intentionally
            dispatch(setQzStateInfo({
                "is_active": false,
                "printer": "",
                "qz": ""
            }));
        }
    };

    // Keep active socket settings verified with Redux states on configuration changes
    useEffect(() => {
        if (qz.websocket.isActive() && !qzState.is_active) {
            dispatch(setQzStateInfo({
                "is_active": true,
                "qz": qz,
                "printer": qzState.printer || ""
            }));
        }
    }, [qzState.is_active, dispatch]);

    const isWorking = localLoading || qzState.progress;

    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box', p: 2.5 }}>
            <SettingsContainer elevation={0}>
                <SectionHeader 
                    icon={<SettingsIcon sx={{ fontSize: '20px' }} />} 
                    title="Printing Configuration" 
                    subtitle="Manage your local printing environment and QZ Tray integration for direct label output" 
                />
                
                <Divider sx={{ my: 3, borderColor: '#E2E8F0' }} />

                <Grid container justifyContent="center">
                    <Grid item xs={12}>
                        <Stack spacing={3.5}>
                            
                            {/* 1. Status Section (Active or Inactive) */}
                            <StatusBlock isActive={qzState.is_active} sx={{ minHeight: '44px' }}>
                                <Stack direction="row" spacing={2.5} alignItems="center">
                                    <Box sx={{ 
                                        p: 1.5, 
                                        bgcolor: qzState.is_active ? '#2E7D32' : '#64748B', 
                                        borderRadius: '10px', 
                                        color: '#FFF',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '38px',
                                        height: '38px',
                                        boxSizing: 'border-box'
                                    }}>
                                        {isWorking ? <CircularProgress size={18} color="inherit" thickness={5} /> : <LocalPrintshopIcon sx={{ fontSize: '18px' }} />}
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', fontSize: '14.5px', letterSpacing: '-0.1px' }}>
                                            {qzState.is_active ? "QZ Mode Enabled" : "Browser Mode Active"}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block', mt: 0.5, fontSize: '12.5px' }}>
                                            {qzState.is_active ? (
                                                <>
                                                    Printer: {qzState.printer || "Ready"} 
                                                    {qzState.queue_count >= 0 && ` • Queue: ${qzState.queue_count} jobs`}
                                                </>
                                            ) : (
                                                "Using standard browser-based printing dialogs"
                                            )}
                                        </Typography>
                                    </Box>
                                </Stack>
                                
                                <Button 
                                    variant={qzState.is_active ? "outlined" : "contained"} 
                                    size="small"
                                    startIcon={!isWorking && <SwitchIcon sx={{ fontSize: '16px !important' }} />}
                                    onClick={qzState.is_active ? handleDisconnectQZ : handleConnectQZ}
                                    disabled={isWorking}
                                    sx={{ 
                                        borderRadius: '10px', 
                                        textTransform: 'none', 
                                        fontWeight: 700,
                                        fontSize: '13px',
                                        height: '38px',
                                        px: 3,
                                        boxShadow: 'none',
                                        borderColor: qzState.is_active ? '#E2E8F0' : 'none',
                                        backgroundColor: qzState.is_active ? '#FFFFFF' : '#1976d2',
                                        color: qzState.is_active ? '#475569' : '#FFFFFF',
                                        whiteSpace: 'nowrap',
                                        '&:hover': { 
                                            backgroundColor: qzState.is_active ? '#F8FAFC' : '#115293',
                                            borderColor: qzState.is_active ? '#CBD5E1' : 'none',
                                            boxShadow: 'none'
                                        },
                                        '&.Mui-disabled': {
                                            backgroundColor: '#E2E8F0',
                                            color: '#94A3B8',
                                            borderColor: 'transparent'
                                        }
                                    }}
                                >
                                    {isWorking ? "Processing..." : (qzState.is_active ? "Switch to Browser" : "Switch to QZ")}
                                </Button>
                            </StatusBlock>

                            {/* 2. Download Section */}
                            <Box 
                                component="a" 
                                href="https://qz.io/download/" 
                                target="_blank"
                                sx={{ 
                                    textDecoration: 'none',
                                    display: 'block',
                                    p: 3,
                                    borderRadius: '12px',
                                    border: '1px dashed #E2E8F0',
                                    backgroundColor: '#FFFFFF',
                                    transition: 'all 0.15s ease-in-out',
                                    cursor: 'pointer',
                                    '&:hover': { 
                                        bgcolor: '#F8FAFC', 
                                        borderColor: '#1976d2',
                                        '& .download-arrow-btn': {
                                            backgroundColor: '#1976d2',
                                            color: '#FFFFFF'
                                        }
                                    }
                                }}
                            >
                                <Stack direction="row" spacing={2.5} alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', gap: 2 }}>
                                    <Stack direction="row" spacing={2.5} alignItems="center">
                                        <Box sx={{ color: '#64748B', display: 'flex', alignItems: 'center' }}>
                                            <AirplayOutlinedIcon sx={{ fontSize: '24px' }} />
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', fontSize: '13.5px' }}>
                                                Download QZ Tray Desktop Client
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500, display: 'block', mt: 0.5, fontSize: '12.5px' }}>
                                                Direct thermal printing workflow execution requires the latest version of QZ Tray installed on your local workstation.
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    <IconButton 
                                        className="download-arrow-btn"
                                        size="small" 
                                        tabIndex={-1}
                                        sx={{ 
                                            bgcolor: 'rgba(25, 118, 210, 0.06)', 
                                            color: '#1976d2',
                                            p: 1,
                                            borderRadius: '8px',
                                            transition: 'all 0.15s ease-in-out'
                                        }}
                                    >
                                        <FileDownloadOutlinedIcon sx={{ fontSize: '18px' }} />
                                    </IconButton>
                                </Stack>
                            </Box>

                        </Stack>
                    </Grid>
                </Grid>

                {/* Footer Insight */}
                <Box sx={{ mt: 5, textAlign: 'center', pt: 2, borderTop: '1px solid #F1F5F9' }}>
                    <Typography variant="caption" sx={{ color: '#94A3B8', fontStyle: 'italic', fontWeight: 500, fontSize: '12px' }}>
                        Note: Direct hardware socket piping significantly optimizes operational throughput speeds by bypassing standard operating system print dialog frames.
                    </Typography>
                </Box>
            </SettingsContainer>
        </Box>
    );
};

export default QZTrayPage;